import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Calendar, FileText, Settings, LogOut, ChevronRight, 
  Clock, Users, Euro, AlertCircle, Check, X, Loader2, Star, MessageSquare 
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [bookingToCancelDetails, setBookingToCancelDetails] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancellationResult, setCancellationResult] = useState(null);
  
  // Review states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [bookingToReview, setBookingToReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewAuthorName, setReviewAuthorName] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewedBookings, setReviewedBookings] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchBookings();
    }
  }, [user, authLoading, navigate]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/my-bookings`, {
        
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const openCancelDialog = (booking) => {
    setBookingToCancel(booking.id);
    setBookingToCancelDetails(booking);
    setCancellationResult(null);
    setCancelDialogOpen(true);
  };

  const handleCancelBooking = async () => {
    if (!bookingToCancel) return;
    
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/bookings/${bookingToCancel}/cancel`,
        {},
        
      );
      setCancellationResult(response.data);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
      setCancelDialogOpen(false);
    } finally {
      setCancelling(false);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setBookingToCancel(null);
    setBookingToCancelDetails(null);
    setCancellationResult(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Check if a booking is eligible for review (after checkout date)
  const canLeaveReview = (booking) => {
    if (booking.status !== 'confirmed') return false;
    if (reviewedBookings.includes(booking.id)) return false;
    const checkoutDate = new Date(booking.check_out);
    const today = new Date();
    return today >= checkoutDate;
  };

  const openReviewDialog = (booking) => {
    setBookingToReview(booking);
    setReviewRating(5);
    setReviewComment('');
    setReviewAuthorName(user?.name || '');
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewComment.trim()) {
      toast.error('Veuillez écrire un commentaire');
      return;
    }
    if (reviewComment.length < 20) {
      toast.error('Votre commentaire doit contenir au moins 20 caractères');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API_URL}/api/reviews/submit`, {
        booking_id: bookingToReview.id,
        rating: reviewRating,
        comment: reviewComment,
        author_name: reviewAuthorName || null
      });
      
      toast.success('Merci pour votre avis ! Il sera publié après validation.');
      setReviewedBookings(prev => [...prev, bookingToReview.id]);
      setReviewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'avis');
    } finally {
      setSubmittingReview(false);
    }
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (status === 'cancelled') {
      return <span className="status-badge status-cancelled">Annulée</span>;
    }
    if (status === 'cancellation_requested') {
      return <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Annulation en cours</span>;
    }
    if (status === 'confirmed' && paymentStatus === 'paid') {
      return <span className="status-badge status-confirmed">Confirmée</span>;
    }
    // Pending = en attente de paiement
    return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">En attente de paiement</span>;
  };

  // Handle payment for pending booking
  const handlePayPendingBooking = async (bookingId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/create-checkout?booking_id=${bookingId}`
      );
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#FAFAFA]" data-testid="client-dashboard">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white py-12">
        <div className="container-custom">
          <h1 className="text-3xl md:text-4xl font-['Playfair_Display'] mb-2">Espace client</h1>
          <p className="text-white/70 font-['Outfit']">Bienvenue, {user?.name || user?.email}</p>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E5E5E5] p-6">
              <nav className="space-y-2">
                <a 
                  href="#bookings" 
                  className="flex items-center gap-3 p-3 bg-[#FAFAFA] text-[#0A0A0A] font-['Outfit']"
                  data-testid="nav-bookings"
                >
                  <Calendar className="w-5 h-5 text-[#C5A059]" />
                  Mes réservations
                </a>
                <a 
                  href="#account" 
                  className="flex items-center gap-3 p-3 hover:bg-[#FAFAFA] text-[#666666] font-['Outfit'] transition-colors"
                  data-testid="nav-account"
                >
                  <Settings className="w-5 h-5" />
                  Mon compte
                </a>
                <button 
                  onClick={handleLogout}
                  className="flex items-center gap-3 p-3 hover:bg-red-50 text-red-600 font-['Outfit'] w-full transition-colors"
                  data-testid="logout-sidebar"
                >
                  <LogOut className="w-5 h-5" />
                  Déconnexion
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Bookings Section */}
            <div id="bookings" className="bg-white border border-[#E5E5E5]">
              <div className="p-6 border-b border-[#E5E5E5] flex justify-between items-center">
                <h2 className="text-xl font-['Playfair_Display']">Mes réservations</h2>
                <Button 
                  onClick={() => navigate('/booking')}
                  className="bg-[#C5A059] hover:bg-[#A6864A] text-white"
                  data-testid="new-booking-btn"
                >
                  Nouvelle réservation
                </Button>
              </div>

              {bookings.length === 0 ? (
                <div className="p-12 text-center">
                  <Calendar className="w-12 h-12 text-[#E5E5E5] mx-auto mb-4" />
                  <p className="text-[#666666] font-['Outfit']">Aucune réservation pour le moment</p>
                  <Button 
                    onClick={() => navigate('/booking')}
                    variant="outline"
                    className="mt-4 border-[#C5A059] text-[#C5A059]"
                    data-testid="first-booking-btn"
                  >
                    Réserver maintenant
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-[#E5E5E5]">
                  {bookings.map((booking, index) => (
                    <div key={index} className="p-6" data-testid={`booking-${index}`}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {getStatusBadge(booking.status, booking.payment_status)}
                            <span className="text-sm text-[#666666] font-['Outfit']">
                              Réf: {booking.id || `#${index + 1}`}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-[#C5A059]" />
                              <div className="font-['Outfit']">
                                <p className="text-[#666666]">Arrivée</p>
                                <p className="text-[#0A0A0A]">{format(new Date(booking.check_in), 'd MMM yyyy', { locale: fr })}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-[#C5A059]" />
                              <div className="font-['Outfit']">
                                <p className="text-[#666666]">Départ</p>
                                <p className="text-[#0A0A0A]">{format(new Date(booking.check_out), 'd MMM yyyy', { locale: fr })}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-[#C5A059]" />
                              <div className="font-['Outfit']">
                                <p className="text-[#666666]">Invités</p>
                                <p className="text-[#0A0A0A]">{booking.guests} pers.</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Euro className="w-4 h-4 text-[#C5A059]" />
                              <div className="font-['Outfit']">
                                <p className="text-[#666666]">Total</p>
                                <p className="text-[#0A0A0A] font-semibold">{booking.total_price}€</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {/* Pay button - only for pending bookings */}
                          {booking.status === 'pending' && booking.payment_status !== 'paid' && (
                            <Button 
                              className="bg-[#C5A059] hover:bg-[#A6864A] text-white"
                              onClick={() => handlePayPendingBooking(booking.id)}
                              data-testid={`pay-booking-${index}`}
                            >
                              <Euro className="w-4 h-4 mr-1" />
                              Payer maintenant
                            </Button>
                          )}
                          {/* Review button - only for completed stays */}
                          {canLeaveReview(booking) && (
                            <Button 
                              variant="outline"
                              className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10"
                              onClick={() => openReviewDialog(booking)}
                              data-testid={`review-booking-${index}`}
                            >
                              <Star className="w-4 h-4 mr-1" />
                              Laisser un avis
                            </Button>
                          )}
                          {booking.status !== 'cancelled' && booking.status !== 'cancellation_requested' && booking.status !== 'pending' && (
                            <Button 
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              onClick={() => openCancelDialog(booking)}
                              data-testid={`cancel-booking-${index}`}
                            >
                              Annuler
                            </Button>
                          )}
                          {booking.status === 'cancellation_requested' && (
                            <span className="text-xs text-orange-600 font-['Outfit']">
                              Demande d'annulation en cours de traitement...
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Account Section */}
            <div id="account" className="bg-white border border-[#E5E5E5] mt-8">
              <div className="p-6 border-b border-[#E5E5E5]">
                <h2 className="text-xl font-['Playfair_Display']">Mon compte</h2>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-[#666666] font-['Outfit']">Nom</p>
                    <p className="text-[#0A0A0A] font-['Outfit']">{user?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#666666] font-['Outfit']">Email</p>
                    <p className="text-[#0A0A0A] font-['Outfit']">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#666666] font-['Outfit']">Téléphone</p>
                    <p className="text-[#0A0A0A] font-['Outfit']">{user?.phone || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={(open) => !open && closeCancelDialog()}>
        <DialogContent className="sm:max-w-md">
          {!cancellationResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display']">Demande d'annulation</DialogTitle>
                <DialogDescription className="font-['Outfit']">
                  Êtes-vous sûr de vouloir demander l'annulation de cette réservation ?
                  {bookingToCancelDetails && (
                    <div className="mt-4 p-4 bg-[#FAFAFA] border border-[#E5E5E5] rounded">
                      <p className="font-semibold text-[#0A0A0A] mb-2">
                        Séjour du {bookingToCancelDetails.check_in} au {bookingToCancelDetails.check_out}
                      </p>
                      <p className="text-[#666666]">Montant total : {bookingToCancelDetails.total_price}€</p>
                    </div>
                  )}
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded text-sm">
                    <p className="font-semibold mb-2 text-amber-800">Politique d'annulation :</p>
                    <ul className="space-y-1 text-amber-700">
                      <li>• Plus de 14 jours avant l'arrivée : <strong>100% remboursé</strong></li>
                      <li>• Entre 7 et 14 jours avant l'arrivée : <strong>50% remboursé</strong></li>
                      <li>• Moins de 7 jours avant l'arrivée : <strong>Non remboursable</strong></li>
                    </ul>
                  </div>
                  <p className="mt-4 text-xs text-[#666] italic">
                    Votre demande sera traitée par l'équipe du Domaine du Normont. Le remboursement sera effectué après validation.
                  </p>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={closeCancelDialog}>
                  Retour
                </Button>
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleCancelBooking}
                  disabled={cancelling}
                  data-testid="confirm-cancel-btn"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {cancelling ? 'Envoi en cours...' : 'Envoyer la demande'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
                  <Clock className="w-6 h-6 text-orange-500" />
                  Demande envoyée
                </DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <div className="p-6 bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-['Playfair_Display'] mb-2">Votre demande d'annulation a été envoyée</h3>
                  <p className="text-sm text-[#666] mb-4">L'équipe du Domaine du Normont va traiter votre demande dans les plus brefs délais.</p>
                  
                  <div className="mt-4 p-4 bg-white border border-[#E5E5E5] rounded text-left">
                    <p className="text-sm text-[#666666] font-['Outfit'] mb-2">
                      <strong>Politique applicable :</strong>
                    </p>
                    <p className="text-[#0A0A0A] font-['Outfit'] font-medium">
                      {cancellationResult.refund_message}
                    </p>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="p-3 bg-white border border-[#E5E5E5] rounded">
                      <p className="text-xs text-[#666666] font-['Outfit']">Montant total</p>
                      <p className="text-lg font-semibold font-['Outfit']">{cancellationResult.total_price}€</p>
                    </div>
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                      <p className="text-xs text-orange-700 font-['Outfit']">Remboursement prévu</p>
                      <p className="text-lg font-semibold text-orange-700 font-['Outfit']">
                        {cancellationResult.refund_amount}€ ({cancellationResult.refund_percent}%)
                      </p>
                    </div>
                  </div>
                  
                  <p className="mt-4 text-sm text-[#666666] font-['Outfit']">
                    Vous recevrez un email de confirmation une fois votre demande traitée.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  className="w-full bg-[#C5A059] hover:bg-[#A6864A] text-white"
                  onClick={closeCancelDialog}
                >
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']">Laisser un avis</DialogTitle>
            <DialogDescription className="font-['Outfit']">
              Partagez votre expérience au Domaine du Normont
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Star Rating */}
            <div>
              <Label className="font-['Outfit'] mb-3 block">Votre note</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    className="transition-transform hover:scale-110"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= reviewRating ? 'fill-[#C5A059] text-[#C5A059]' : 'text-gray-300'}`} 
                    />
                  </button>
                ))}
              </div>
            </div>
            
            {/* Author Name */}
            <div>
              <Label className="font-['Outfit'] mb-2 block">Votre nom (affiché avec l'avis)</Label>
              <input 
                type="text"
                value={reviewAuthorName}
                onChange={(e) => setReviewAuthorName(e.target.value)}
                placeholder={user?.name || 'Votre nom'}
                className="w-full border border-[#E5E5E5] p-3 font-['Outfit'] rounded focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                data-testid="review-author-input"
              />
            </div>
            
            {/* Comment */}
            <div>
              <Label className="font-['Outfit'] mb-2 block">Votre commentaire</Label>
              <textarea 
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Racontez-nous votre séjour au Domaine du Normont..."
                className="w-full border border-[#E5E5E5] p-3 font-['Outfit'] rounded min-h-[120px] resize-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                data-testid="review-comment-input"
              />
              <p className="text-xs text-[#999] font-['Outfit'] mt-1">
                Minimum 20 caractères ({reviewComment.length} / 20)
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-[#C5A059] hover:bg-[#A6864A] text-white"
              onClick={handleSubmitReview}
              disabled={submittingReview || reviewComment.length < 20}
              data-testid="submit-review-btn"
            >
              {submittingReview ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
              ) : (
                <>Envoyer mon avis</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
